/**
 * Build the `ApiError` the API hooks throw from whatever JSON body the gateway
 * returned (H-1).
 *
 * The backend answers a failed request in one of three shapes:
 *   1. `{ message, code?, details? }`          — the hooks' own ApiError shape;
 *   2. `{ error: "..." }`                       — the legacy hand-rolled module
 *      envelope (thousands of `new { error = ... }` call sites, burning down
 *      behind the `ErrorLiteralRatchetTests` shrink-only ratchet);
 *   3. RFC-7807 ProblemDetails `{ title, detail, status, ... }` with Tyndo
 *      extensions (`error`, `code`/`errorCode`, `fieldName`) — the Gateway
 *      ExceptionMiddleware and the `TyndoProblems` helper.
 *
 * Before this module, only shape 1 surfaced its message — shapes 2 and 3
 * collapsed to the generic HTTP `statusText` ("Bad Request") and the operator
 * never saw WHY the request failed. The precedence here is therefore:
 * `message → error → detail → title → statusText`.
 *
 * Kept hook-free in its own module (like `extractErrorMessage`) so it is
 * unit-testable under Node's `--experimental-strip-types` runner; `useApi.ts`
 * imports it for the non-2xx path.
 */

/**
 * Structural twin of `ApiError` in `useApi.ts` — declared here (rather than
 * imported) so this module stays free of the react/tanstack import graph.
 */
export interface ApiErrorShape {
  message: string;
  code?: string;
  statusCode?: number;
  details?: Record<string, unknown>;
}

/**
 * The concrete value the API hooks throw — a REAL `Error` subclass.
 *
 * Why a class and not the plain object literal this module used to return:
 * the reason the gateway supplies was being extracted correctly here and then
 * DISCARDED at the last render step. `ErrorState` (and ~190 hand-written
 * `error instanceof Error ? error.message : 'Unknown error'` ternaries) narrow
 * on `instanceof Error`. A plain object fails that test, so a governed refusal
 * — an SoD denial, an ISA-18.2 shelving refusal, any RFC-7807 `detail` — was
 * rendered to the operator as "An unknown error occurred". Making the thrown
 * value a genuine `Error` makes every one of those existing narrowings correct
 * at once, without touching a single call site.
 *
 * The shape is unchanged: `message` / `code` / `statusCode` / `details` are the
 * same fields, so it remains structurally assignable to `ApiError`
 * (`useApi.ts`) and every `error.statusCode` / `error.code` branch keeps
 * working. `problemDetails` is additive — the raw RFC-7807 body, kept so a
 * caller can read a Tyndo extension the flattening did not lift.
 */
export class ApiRequestError extends Error implements ApiErrorShape {
  /** Machine-readable code (`code` / `errorCode` from the body, or the auth default). */
  readonly code?: string;
  /** HTTP status of the failed response. */
  readonly statusCode?: number;
  /** Structured extras (`details`, plus a lifted top-level `fieldName`). */
  readonly details?: Record<string, unknown>;
  /** The raw error body as parsed, when it was a JSON object. */
  readonly problemDetails?: Record<string, unknown>;

  constructor(init: ApiErrorShape & { problemDetails?: Record<string, unknown> }) {
    super(init.message);
    // `name` is what `String(err)` and console.error prefix with; keep the
    // long-standing public label rather than the class identifier.
    this.name = 'ApiError';
    this.code = init.code;
    this.statusCode = init.statusCode;
    this.details = init.details;
    this.problemDetails = init.problemDetails;
    // Downlevel-safe prototype restoration: a class extending a builtin loses
    // its prototype chain when compiled to ES5, which would make the
    // `instanceof Error` narrowing this whole change exists for silently false
    // again under an older build target.
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Runtime predicate for the thrown value, for callers that want the `code` /
 * `statusCode` fields narrowed rather than just the message. `instanceof
 * Error` already suffices to read `.message`; this is the stronger test.
 */
export function isApiRequestError(value: unknown): value is ApiRequestError {
  return value instanceof ApiRequestError;
}

const firstNonEmptyString = (...candidates: unknown[]): string | undefined => {
  for (const c of candidates) {
    if (typeof c === 'string' && c.length > 0) return c;
  }
  return undefined;
};

export function toApiError(
  body: unknown,
  statusCode: number,
  statusText: string,
): ApiRequestError {
  const isObjectBody = Boolean(body) && typeof body === 'object' && !Array.isArray(body);
  const e = (isObjectBody ? body : {}) as Record<string, unknown>;

  const message =
    firstNonEmptyString(e.message, e.error, e.detail, e.title) ?? statusText;
  // The gateway's ProblemDetails envelope uses `errorCode`; module endpoints
  // and the hooks' own shape use `code`. Accept both.
  const code = firstNonEmptyString(e.code, e.errorCode);

  let details =
    e.details && typeof e.details === 'object' && !Array.isArray(e.details)
      ? (e.details as Record<string, unknown>)
      : undefined;
  // Validation problems name the offending request field at the top level;
  // surface it through `details` so callers can highlight the input.
  if (typeof e.fieldName === 'string' && e.fieldName.length > 0) {
    details = { ...details, fieldName: e.fieldName };
  }

  return new ApiRequestError({
    message,
    code,
    statusCode,
    details,
    problemDetails: isObjectBody ? e : undefined,
  });
}

/**
 * `toApiError` with the 401/403 guarantees the hooks' public contract pins:
 *
 *   - the thrown `code` is always present for auth failures
 *     (`'unauthorized'` / `'forbidden'`) unless the body carries its own
 *     `code`/`errorCode` — call sites branch on `error.code === 'forbidden'`;
 *   - the message falls back to "Unauthorized"/"Forbidden" even when the
 *     transport gives no statusText (HTTP/2 has none), so a bodiless auth
 *     failure never surfaces an empty string.
 *
 * Crucially the BODY still wins: a 403 ProblemDetails from an SoD /
 * segregation-of-duties gate (`detail: "Reviewer cannot approve their own
 * change"`) surfaces its real explanation instead of collapsing to the
 * generic "Forbidden" the hooks used to hard-code.
 */
export function toAuthAwareApiError(
  body: unknown,
  statusCode: number,
  statusText: string,
): ApiRequestError {
  const authFallback =
    statusCode === 401 ? 'Unauthorized' : statusCode === 403 ? 'Forbidden' : '';
  const error = toApiError(body, statusCode, statusText || authFallback);
  if (error.code !== undefined) return error;
  const authCode =
    statusCode === 401 ? 'unauthorized' : statusCode === 403 ? 'forbidden' : undefined;
  if (authCode === undefined) return error;
  return new ApiRequestError({
    message: error.message,
    code: authCode,
    statusCode: error.statusCode,
    details: error.details,
    problemDetails: error.problemDetails,
  });
}
